import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Avatar } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { FormCard } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import dayjs from 'dayjs'
import { StockRecord } from '../../types'

type AdministrationDetailContentProps = {
  record: StockRecord
  medicationName: string
}

export const AdministrationDetailContent = ({
  record
}: AdministrationDetailContentProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const quantity = Math.abs(record.quantityChange) || 0
  const unit = record.unit || t('TABLETS')
  const signedByName = record.recordedBy.name || ''
  const timeLogged = dayjs(record.date).format('DD-MM-YYYY HH:mm')
  const witnesses = record.witnesses || []

  const comment = Array.isArray(record.notes)
    ? record.notes.join(', ')
    : record.notes || ''

  const administeredText = `${quantity} ${unit} ${t('ADMINISTERED')}`

  const hasStockChanged = record.quantityChange !== 0
  const stockChangedText = hasStockChanged
    ? `${t('YES')}, removed ${Math.abs(record.quantityChange)} ${unit} from stock`
    : t('NO')

  return (
    <SectionContainer>
      <FormCard level={2}>
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('SCHEDULED_FOR')}</StyledLabel>
            <Text>-</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>
              {t('HOW_MANY_WAS_ADMINISTERED')}
            </StyledLabel>
            <Text>{administeredText}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('HAS_STOCK_AMOUNT_CHANGED')}</StyledLabel>
            <Text>{stockChangedText}</Text>
          </InfoSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <FullWidthSection>
            <StyledLabel level={2}>{t('COMMENT')}</StyledLabel>
            <Text>{comment || '-'}</Text>
          </FullWidthSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('TIME_RECORDED')}</StyledLabel>
            <Text>{timeLogged}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('SIGNED_BY')}</StyledLabel>
            <SignedByContainer>
              <Avatar
                url={record.recordedBy.picture as unknown as string}
                size={16}
              />
              <Text weight="bold">{signedByName || '-'}</Text>
            </SignedByContainer>
          </InfoSection>
          {record.witnessed && witnesses.length > 0 && (
            <InfoSection>
              <StyledLabel level={2}>{t('WITNESSED_BY')}</StyledLabel>
              <WitnessesContainer>
                {witnesses.map((witness, index) => (
                  <WitnessContainer key={index}>
                    <Avatar
                      url={(witness as any).picture as unknown as string}
                      size={16}
                    />
                    <Text weight="bold">{(witness as any).name || '-'}</Text>
                  </WitnessContainer>
                ))}
              </WitnessesContainer>
            </InfoSection>
          )}
        </InfoRow>
      </FormCard>
    </SectionContainer>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RowDivider = styled(Divider)`
  margin: -8px 0;
`

const SignedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const WitnessesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WitnessContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
